import { motion } from "framer-motion";
import { IMAGES } from "./data";
import { Reveal, Eyebrow, EASE } from "./Reveal";
import { scriptureLink } from "@/data/scripture";

const CHAPTERS = [
  {
    num: "01",
    title: "To Jesus through Mary",
    text: "Mary never keeps anything for herself. Every title, feast, shrine, and prayer in this treasury exists to do one thing: lead a person to her Son. As she said at Cana — “Do whatever he tells you.” (John 2:5, RSV-CE). This is the measure of everything we publish.",
    scripture: "John 2:5",
  },
  {
    num: "02",
    title: "Faithful and clearly sourced",
    text: "We draw from the Catechism, Sacred Scripture, documents of the Holy See, and official diocesan and shrine sources. History, testimony, doctrine, and devotion are labelled for what they are — and every apparition carries its exact Church status, never a vague “approved.”",
  },
  {
    num: "03",
    title: "Open to every nation",
    text: "Inspired by the exhibition of St. Carlo Acutis, this treasury gathers Marian devotion from Mexico to Rwanda, Lithuania to Portugal — one Church, one Mother, prayed in every language on earth. It is built to welcome scholars, parishes, the elderly, and the simply curious alike.",
  },
];

export default function Manifesto() {
  return (
    <section id="discover" data-testid="manifesto-chapters" className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <Eyebrow>Our charter</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl font-medium leading-[1.08] tracking-tight text-midnight sm:text-4xl lg:text-5xl">
                A treasury, not a catalogue
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-charcoal/85 sm:text-lg">
                Three commitments govern every page of this work — written before a single
                article, and reviewed with every one.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <figure className="mt-10">
                <div className="arch-frame overflow-hidden border border-gold/40 shadow-[0_36px_70px_-36px_rgba(16,42,67,0.5)]">
                  <motion.img
                    src={IMAGES.murillo.url}
                    alt={IMAGES.murillo.alt}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover"
                    initial={{ scale: 1.12 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease: EASE }}
                  />
                </div>
                <figcaption className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slateblue">
                  {IMAGES.murillo.credit}
                </figcaption>
              </figure>
            </Reveal>
          </div>

          <div>
            {CHAPTERS.map((chapter, i) => (
              <Reveal key={chapter.num} delay={i * 0.08} className="group border-t border-marian/15 py-10 first:border-t-0 first:pt-2 last:pb-0">
                <div className="flex items-start gap-6 sm:gap-9">
                  <span
                    className="font-serif text-5xl font-medium leading-none text-gold/50 transition-colors duration-500 group-hover:text-gold sm:text-7xl"
                    aria-hidden="true"
                  >
                    {chapter.num}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl font-semibold text-midnight sm:text-3xl">
                      {chapter.title}
                    </h3>
                    <p data-testid={`manifesto-text-${chapter.num}`} className="mt-4 max-w-lg text-base leading-relaxed text-charcoal/85">
                      {chapter.text}
                    </p>
                    {chapter.scripture && <a href={scriptureLink(chapter.scripture)} target="_blank" rel="noopener noreferrer" data-testid={`manifesto-scripture-source-${chapter.num}`} className="mt-3 inline-block text-xs text-marian underline underline-offset-4 transition-colors hover:text-gold">{chapter.scripture} · RSV-CE · excerpt</a>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
